import * as React from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { UniverseModuleType } from 'src/domains/common/universes.type';
import { observer } from 'src/utils/mobx-react';

type ComponentInType<PropsType> = (props: PropsType) => React.ReactNode;
type ComponentOutType<PropsType> = (props: PropsType) => React.ReactElement;

const computedWithCache = <P, R>(computedResult: (params: P) => R): ((params: P) => R) => {
    let cache: null | [P, R] = null;

    return (params: P): R => {
        if (cache === null) {
            const result = computedResult(params);
            cache = [params, result];
            return result;
        }

        const [oldParams, oldResult] = cache;

        if (oldParams === params) {
            return oldResult;
        }

        const result = computedResult(params);
        cache = [params, result];
        return result;
    };
};

export const withConfig = <PropsType extends {}>(
    buildComponent: (c: UniverseModuleType['themeColors']) => ComponentInType<PropsType>
): ComponentOutType<PropsType> => {
    const getComponent = computedWithCache(buildComponent);

    const WithConfig = observer(
        'WithConfig',
        (props: PropsType & { forwardedRef: React.ForwardedRef<unknown> }): React.ReactElement => {
            const appState = useAppStateContext();

            const theme = appState.universeModule.themeColors;
            const Component = getComponent(theme);

            const { forwardedRef, ...propsInner } = props;

            // eslint-disable-next-line react/jsx-props-no-spreading
            return (
                //@ts-expect-error
                <Component
                    {...propsInner}
                    ref={forwardedRef}
                />
            );
        }
    );
    //@ts-expect-error - ForwardRefExoticComponent is not assignable to ComponentOutType
    return React.forwardRef((props, ref) => {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return (
            //@ts-expect-error - PropsWithoutRef<PropsType> is not assignable to PropsType in generic context
            <WithConfig
                {...props}
                forwardedRef={ref}
            />
        );
    });
};
